import {
  Background,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutGrid, RefreshCw, RotateCcw } from "lucide-react";
import CustomNode from "./custom-node";
import "./diagram.css";
import DummyNode from "./dummy-node";
import InteractionPanel from "./interaction-panel";
import useHoverEffects from "./hooks/useHoverEffects";
import useLayoutedElements from "./hooks/useLayoutedElements";
import useNodeLayoutPersistence from "./hooks/useNodeLayoutPersistence";
import useSelectionEffects from "./hooks/useSelectionEffects";
export default function Diagram({
  services = [],
  userId = null,
  projectId = null,
  onRefresh = null,
  refreshing = false,
}) {
  const { getPositions, savePosition, clearPositions } =
    useNodeLayoutPersistence(userId, projectId);

  const initialNodes = useMemo(() => {
    const serviceIds = new Set(services.map((s) => s.id || s.title));
    const stored = getPositions();

    const normalNodes = services.map((service, index) => {
      const id = service.id || service.title;
      const storedPos = stored[id];
      return {
        id,
        type: "customNode",
        position: storedPos || {
          x: (index % 3) * 300,
          y: Math.floor(index / 3) * 200,
        },
        data: {
          title: service.title,
          name: service.name || service.title,
          platform: service.platform,
          inbound: service.inbounds?.length || 0,
          outbound: service.outbounds?.length || 0,
          version: service.version,
          warnings: service.warnings || 0,
          type: service.type,
          errors: service.outbounds.reduce(
            (acc, curr) => acc + curr.contractMatchingErrors.length,
            0
          ),
          tags: service.tags,
        },
      };
    });
    // Find all missing targets from outbounds
    const missingTargets = new Set(
      services.flatMap((service) =>
        service.outbounds
          .map((outbound) => outbound.service)
          .filter((target) => !serviceIds.has(target))
      )
    );
    // Create dummy nodes for missing targets
    const dummyNodes = Array.from(missingTargets).map((target) => ({
      id: target,
      type: "dummyNode",
      position: stored[target] || { x: 150, y: 5 },
      data: {
        title: target,
        isPlaceholder: true,
        warning: `Undefined service: ${target}`,
      },
    }));

    return [...normalNodes, ...dummyNodes];
  }, [services, getPositions]);

  const initialEdges = useMemo(() => {
    const serviceIds = new Set(services.map((s) => s.id || s.title));

    return services.flatMap((service) =>
      service.outbounds.map((outbound) => {
        const targetExists = serviceIds.has(outbound.service);
        const isProblematic = outbound?.contractMatchingErrors.length > 0;

        // Determine edge style based on conditions
        let edgeStyle = {
          stroke: "#96EF48",
          strokeWidth: 4,
        };

        if (!targetExists) {
          edgeStyle = {
            stroke: "#FFD700", // Yellow warning color
            strokeDasharray: "8 4",
            strokeWidth: 4,
          };
        } else if (isProblematic) {
          edgeStyle.stroke = "red";
        }

        return {
          id: `e-${service.title}-${outbound.service}`,
          source: service.title,
          target: outbound.service,
          type: "smoothstep",
          style: edgeStyle,
          label: outbound.targetUrl || "",
          labelStyle: {
            fill: "#8C8C8C",
            fontSize: 10,
            fontFamily: "JetBrains Mono, monospace",
          },
          labelBgStyle: {
            fill: "#1B1E27",
            fillOpacity: 0.85,
          },
          labelBgPadding: [4, 2],
          labelBgBorderRadius: 3,
          data: {
            contractMatchingErrors: outbound.contractMatchingErrors,
            isUndefinedTarget: !targetExists,
            targetUrl: outbound.targetUrl,
          },
          originalColor: edgeStyle.stroke,
        };
      })
    );
  }, [services]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeDimensions, setNodeDimensions] = useState({});
  const [selectedEdge, setSelectedEdge] = useState(null);

  // Refreshing overview data updates `services` -> `initialNodes`/`initialEdges`,
  // but `useNodesState` / `useEdgesState` only read their arg at mount. Sync
  // the latest data into the existing React Flow state so styling (e.g. red
  // edges for new contract errors, green again when fixed) reflects the
  // refreshed data without remounting and losing dragged positions.
  useEffect(() => {
    setNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      return initialNodes.map((next) => {
        const existing = prevById.get(next.id);
        // Preserve user-dragged position; refresh data and any updated style.
        return existing
          ? { ...next, position: existing.position }
          : next;
      });
    });
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  const [dimensionsLoaded, setDimensionsLoaded] = useState(false);
  const dimensionsLoadedRef = useRef(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [initialCenteringComplete, setInitialCenteringComplete] =
    useState(false);
  const updateNodeDimensions = (nodeId, dimensions) => {
    setNodeDimensions((prev) => ({
      ...prev,
      [nodeId]: dimensions,
    }));
  };

  useEffect(() => {
    // Check if all nodes have dimensions
    const allNodesLoaded = initialNodes.every(
      (node) => nodeDimensions[node.id]
    );
    if (allNodesLoaded && !dimensionsLoadedRef.current) {
      dimensionsLoadedRef.current = true;
      setDimensionsLoaded(true);
    }
  }, [nodeDimensions, initialNodes]);

  // Use the layout function returned by useLayoutedElements
  const rawTriggerLayout = useLayoutedElements(setNodes, nodeDimensions);
  const [initialLayoutComplete, setInitialLayoutComplete] = useState(false);

  // Wraps ELK so stored positions always win — ELK runs, then we
  // overwrite ELK's coords with whatever the user had saved per node.
  const triggerLayout = useCallback(
    async (currentNodes, currentEdges) => {
      await rawTriggerLayout(currentNodes, currentEdges);
      const stored = getPositions();
      if (Object.keys(stored).length === 0) return;
      setNodes((prev) =>
        prev.map((n) =>
          stored[n.id] ? { ...n, position: { ...stored[n.id] } } : n
        )
      );
    },
    [rawTriggerLayout, getPositions, setNodes]
  );

  // True when every node already has a stored position — skip ELK entirely.
  const allNodesHaveStoredPositions = useMemo(() => {
    if (!projectId || initialNodes.length === 0) return false;
    const stored = getPositions();
    return initialNodes.every((n) => stored[n.id]);
  }, [projectId, initialNodes, getPositions]);

  useEffect(() => {
    if (dimensionsLoaded && !initialLayoutComplete) {
      if (allNodesHaveStoredPositions) {
        setInitialLayoutComplete(true);
        return;
      }
      triggerLayout(nodes, edges).then(() => {
        setInitialLayoutComplete(true);
      });
    }
  }, [dimensionsLoaded, nodes, edges, triggerLayout, allNodesHaveStoredPositions, initialLayoutComplete]);
  useEffect(() => {
    return () => {
      // Reset layout flags when services change
      setInitialLayoutComplete(false);
      dimensionsLoadedRef.current = false;
      setDimensionsLoaded(false);
    };
  }, [services]);

  const nodeTypes = useMemo(
    () => ({
      customNode: (props) => (
        <CustomNode {...props} setNodeDimensions={updateNodeDimensions} />
      ),
      dummyNode: (props) => (
        <DummyNode {...props} setNodeDimensions={updateNodeDimensions} />
      ),
    }),
    []
  );

  const { onEdgeClick, onPaneClick } = useSelectionEffects(
    nodes,
    setNodes,
    edges,
    setEdges,
    selectedEdge,
    setSelectedEdge
  );

  const {
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onNodeMouseEnter,
    onNodeMouseLeave,
  } = useHoverEffects(nodes, setNodes, edges, setEdges, selectedEdge);

  const closeErrorPanel = () => {
    onPaneClick();
  };

  useEffect(() => {
    if (
      initialLayoutComplete &&
      !initialCenteringComplete &&
      reactFlowInstance &&
      nodes.length > 0
    ) {
      reactFlowInstance.fitView({ duration: 600, padding: 0.15 });

      setInitialCenteringComplete(true);
    }
  }, [
    nodes,
    reactFlowInstance,
    initialLayoutComplete,
    initialCenteringComplete,
  ]);

  // Manual rebalance: clear any saved positions, then run ELK fresh.
  const handleRebalance = useCallback(() => {
    clearPositions();
    rawTriggerLayout(nodes, edges);
  }, [clearPositions, rawTriggerLayout, nodes, edges]);

  const handleNodeDragStop = useCallback(
    (_event, node) => {
      savePosition(node.id, node.position);
    },
    [savePosition]
  );

  const handleResetLayout = useCallback(() => {
    clearPositions();
    rawTriggerLayout(nodes, edges).then(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({ duration: 600, padding: 0.15 });
      }
    });
  }, [clearPositions, rawTriggerLayout, nodes, edges, reactFlowInstance]);

  return (
    <div className="w-full h-[90vh] relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onEdgeClick={onEdgeClick}
        onNodeDragStop={handleNodeDragStop}
        panOnScroll
        proOptions={{ hideAttribution: true }}
        panOnScrollSpeed={0.8}
        onPaneClick={onPaneClick}
        onNodeClick={closeErrorPanel}
        onInit={setReactFlowInstance}
        fitView={false} // Disable automatic fitView
      >
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>
      <div className="absolute right-3 bottom-3 z-[5] flex flex-col gap-2">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh data"
            className="p-2 rounded-lg bg-navbarBackground border border-border/30 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        )}
        <button
          onClick={handleRebalance}
          title="Rebalance layout"
          className="p-2 rounded-lg bg-navbarBackground border border-border/30 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <LayoutGrid size={16} />
        </button>
        <button
          onClick={handleResetLayout}
          title="Reset saved layout"
          className="p-2 rounded-lg bg-navbarBackground border border-border/30 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <RotateCcw size={16} />
        </button>
      </div>
      {selectedEdge && (
        <InteractionPanel edge={selectedEdge} onClose={closeErrorPanel} />
      )}
    </div>
  );
}

Diagram.propTypes = {
  services: PropTypes.array,
  userId: PropTypes.string,
  projectId: PropTypes.string,
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
};
